import type { Command, EditorState } from "prosemirror-state";
import type { Node as PMNode } from "prosemirror-model";
import { history, redo, undo } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import { baseKeymap } from "prosemirror-commands";
import { InputRule, inputRules } from "prosemirror-inputrules";
import { findEmoji, parseSegments } from "@/emoji";
import { DEFAULT_LINE_ATTRS, schema, type LineAttrs } from "./schema";

type InitialLine = Partial<LineAttrs> & { text: string };

const INITIAL_LINES: InitialLine[] = [
  { text: ":fire: Chili :hot_dog:" },
  { text: "2026-04-18", size: 72 },
  { text: ":droplet: 500g", size: 72 },
];

export function buildInitialDoc(): PMNode {
  const lines = INITIAL_LINES.map((l) => {
    const attrs = { ...DEFAULT_LINE_ATTRS, ...l };
    const inline = parseSegments(l.text).flatMap((s) => {
      if (s.kind === "emoji") return [schema.nodes.emoji.create({ slug: s.slug, hex: s.hex })];
      if (s.value.length === 0) return [];
      return [schema.text(s.value)];
    });
    return schema.nodes.line.create(attrs, inline);
  });
  return schema.node("doc", null, lines);
}

export type ActiveLine = { node: PMNode; pos: number; depth: number };

export function activeLine(state: EditorState): ActiveLine | null {
  const $from = state.selection.$from;
  for (let d = $from.depth; d > 0; d--) {
    if ($from.node(d).type.name === "line") {
      return { node: $from.node(d), pos: $from.before(d), depth: d };
    }
  }
  return null;
}

export function setLineAttr<K extends keyof LineAttrs>(key: K, value: LineAttrs[K]): Command {
  return (state, dispatch) => {
    const line = activeLine(state);
    if (!line) return false;
    if (line.node.attrs[key] === value) return true;
    if (dispatch) {
      const tr = state.tr.setNodeMarkup(line.pos, undefined, {
        ...line.node.attrs,
        [key]: value,
      });
      dispatch(tr);
    }
    return true;
  };
}

export function insertEmoji(slug: string, hex: string): Command {
  return (state, dispatch) => {
    const node = schema.nodes.emoji.create({ slug, hex });
    if (dispatch) dispatch(state.tr.replaceSelectionWith(node, false).scrollIntoView());
    return true;
  };
}

const emojiInputRule = new InputRule(/:([a-z0-9_-]+):$/, (state, match, start, end) => {
  const raw = match[1];
  if (!raw) return null;
  const entry = findEmoji(raw.replace(/-/g, "_"));
  if (!entry) return null;
  return state.tr.replaceWith(start, end, schema.nodes.emoji.create({ slug: entry.slug, hex: entry.hex }));
});

export function buildPlugins() {
  return [
    inputRules({ rules: [emojiInputRule] }),
    history(),
    keymap({
      "Mod-z": undo,
      "Mod-y": redo,
      "Mod-Shift-z": redo,
    }),
    keymap(baseKeymap),
  ];
}
